import { PRIMARY_REGION, SupportedRegions, getDomainPrefix, NovaEnvironment } from "@nova/constants"
import { type Conforms, resolveDomain } from "./shared"
import { Configuration } from "./src/configuration"
import { ConfigurationValue } from "./src/types"
import zlib from "zlib"
import { DiscriminatedUnion } from "@nova/util-ts"

// import time magic
const compressedEnvTiny = process.env["ENV_TINY"] ?? process.env["env_tiny"]
if (compressedEnvTiny && !process.env["env_tiny_inited"]) {
    try {
        const decompressedEnvTiny = zlib
            .brotliDecompressSync(new Uint8Array(Buffer.from(compressedEnvTiny, "base64")))
            .toString("utf8")
        const env = JSON.parse(decompressedEnvTiny)
        Object.entries(env).forEach(([key, value]) => (process.env[key] = value as string))

        process.env["env_tiny_inited"] = "true"
    } catch (error) {
        console.error("Failed to init env. ", error)
        throw error
    }
}

export const config = new Configuration()
    /*
        providers are sources of configuration

        adding a provider should signify a new place that we are pulling configuration from
    */
    .addProvider({
        name: "environment",
        get: (_: string, { name }: { provider: "environment"; name: string }) => {
            return process.env[name]
        },
    })
    .addProvider({
        name: "configuration",
        get: (
            _: string,
            envs: DiscriminatedUnion<
                {
                    provider: "configuration"
                } & (
                    | { value: ConfigurationValue }
                    | { compute: () => ConfigurationValue }
                    | { production: ConfigurationValue; nonProduction: ConfigurationValue }
                    | {
                          production: ConfigurationValue
                          staging: ConfigurationValue
                          preview: ConfigurationValue
                          alpha: ConfigurationValue
                          localhost: ConfigurationValue
                      }
                )
            >
        ) => {
            if (envs.value !== undefined) {
                return envs.value
            }
            if (envs.compute !== undefined) {
                return envs.compute()
            }
            const currentEnv = config.get("environmentName")
            if (currentEnv === "production") {
                return envs.production
            } else if (envs.nonProduction !== undefined) {
                return envs.nonProduction
            } else if (currentEnv === "staging") {
                return envs.staging
            } else if (currentEnv === "preview") {
                return envs.preview
            } else if (currentEnv === "alpha") {
                return envs.alpha
            } else {
                return envs.localhost
            }
        },
    })

    /*
        fields are various different fields in our configuration

        these represent individual pieces of configuration
    */
    .addField({
        name: "awsStorageBucketId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_STORAGE_BUCKET_ID",
            },
        ],
    })
    .addField({
        name: "adminApiKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_ADMIN_API_KEY",
            },
        ],
    })
    .addField({
        name: "agolClientSecret",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AGOL_OAUTH_CLIENT_SECRET",
            },
        ],
    })
    .addField({
        name: "agolClientId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AGOL_OAUTH_CLIENT_ID",
            },
        ],
    })
    .addField({
        name: "agoraId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_AGORA_APP_ID",
            },
        ],
    })
    .addField({
        name: "awsRegion",
        type: "string",
        required: true,
        default: SupportedRegions.Canada,
        from: [{ provider: "environment", name: "AWS_REGION" }],
    })
    .addField({
        name: "awsDefaultRegion",
        type: "string",
        required: true,
        from: [
            {
                provider: "configuration",
                value: SupportedRegions.Canada,
            },
        ],
    })
    .addField({
        name: "awsLogGroupName",
        type: "string",
        required: true,
        default: "/nova/localhost/nextjs",
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_CLOUDWATCH_LOGNAME",
            },
        ],
    })
    .addField({
        name: "awsMetricName",
        type: "string",
        required: false,
        default: "/nova/localhost/nextjs",
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_CLOUDWATCH_METRICNAME",
            },
        ],
    })
    .addField({
        name: "awsStepFunctionArnHotspotDetection",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_STEP_FUNCTION_ARN_HOTSPOT_DETECTION",
            },
        ],
    })
    .addField({
        name: "awsStepFunctionArnDataTransfer",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_STEP_FUNCTION_ARN_AZURE_TRANSFER",
            },
        ],
    })
    .addField({
        name: "awsStepFunctionArnVideoTranscoder",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_STEP_FUNCTION_ARN_VIDEO_TRANSCODER",
            },
        ],
    })
    .addField({
        name: "awsStepFunctionArnLiveVideoTranscoder",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_STEP_FUNCTION_ARN_LIVE_VIDEO_TRANSCODER",
            },
        ],
    })
    .addField({
        name: "awsStepFunctionArnOrthomosaicGeneration",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_STEP_FUNCTION_ARN_ORTHOMOSAIC_GENERATOR",
            },
        ],
    })
    .addField({
        name: "awsStepFunctionArnPdfCreator",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_STEP_FUNCTION_ARN_PDF_CREATOR",
            },
        ],
    })
    .addField({
        name: "mapTilerApiKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_MAPTILER_API_KEY",
            },
        ],
    })
    .addField({
        name: "what3WordsApiKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_WHAT_3_WORDS_API_KEY",
            },
        ],
    })
    .addField({
        /**
         * gaID is the google analytics id
         */
        name: "gaID",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_NOVA_GA_MEASUREMENT_ID",
            },
        ],
    })
    .addField({
        name: "environmentName",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_NOVA_ENVIRONMENT_NAME",
            },
        ],
    })
    .addField({
        name: "awsUserPoolWebClientId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_NOVA_AWS_USER_POOL_WEB_CLIENT_ID",
            },
        ],
    })
    .addField({
        name: "awsUserPoolOcaClientId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_USER_POOL_OCA_CLIENT_ID",
            },
        ],
    })
    .addField({
        name: "awsUserPoolMobileClientId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_USER_POOL_MOBILE_CLIENT_ID",
            },
        ],
    })
    .addField({
        name: "awsUserPoolAtlasClientId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_USER_POOL_ATLAS_CLIENT_ID",
            },
        ],
    })
    .addField({
        name: "awsAppleAudIos",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPLE_AUD_IOS",
            },
        ],
    })
    .addField({
        name: "awsAppleAudAndroid",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPLE_AUD_ANDROID",
            },
        ],
    })
    .addField({
        name: "awsAppleAudAtlasIos",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPLE_AUD_ATLAS_IOS",
            },
        ],
    })
    .addField({
        name: "awsAppleAudAtlasAndroid",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPLE_AUD_ATLAS_ANDROID",
            },
        ],
    })
    .addField({
        name: "awsAppleAudWeb",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_APPLE_AUD_WEB",
            },
        ],
    })
    .addField({
        name: "appleServerRedirectUrl",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_APPLE_SERVER_REDIRECT_URL",
            },
        ],
    })
    .addField({
        name: "appleTeamId",
        type: "string",
        required: false,
        default: "974C6JAVZ7",
        from: [
            {
                provider: "environment",
                name: "NOVA_APPLE_TEAM_ID",
            },
        ],
    })
    .addField({
        name: "appleKeyId",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NOVA_APPLE_KEY_ID",
            },
        ],
    })
    .addField({
        name: "awsMicrosoftClientIdMobile",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_MICROSOFT_CLIENT_ID_MOBILE",
            },
        ],
    })
    .addField({
        name: "awsMicrosoftClientSecretMobile",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_MICROSOFT_CLIENT_SECRET_MOBILE",
            },
        ],
    })
    .addField({
        name: "awsMicrosoftClientIdAtlas",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_MICROSOFT_CLIENT_ID_ATLAS",
            },
        ],
    })
    .addField({
        name: "awsMicrosoftClientSecretAtlas",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_MICROSOFT_CLIENT_SECRET_ATLAS",
            },
        ],
    })
    .addField({
        name: "awsMicrosoftClientIdWeb",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_MICROSOFT_CLIENT_ID_WEB",
            },
        ],
    })
    .addField({
        name: "awsMicrosoftClientSecretWeb",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_MICROSOFT_CLIENT_SECRET_WEB",
            },
        ],
    })
    .addField({
        name: "awsMicrosoftTenantId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_MICROSOFT_TENANT_ID",
            },
        ],
    })
    .addField({
        name: "awsCognitoSecretKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_COGNITO_SECRET_KEY",
            },
        ],
    })
    .addField({
        name: "awsGoogleClientIdMobile",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_GOOGLE_CLIENT_ID_MOBILE",
            },
        ],
    })
    .addField({
        name: "awsGoogleClientIdAtlas",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_GOOGLE_CLIENT_ID_ATLAS",
            },
        ],
    })
    .addField({
        name: "tagId",
        type: "string",
        required: true,
        default: "development",
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_NOVA_APP_IMAGE_TAG",
            },
        ],
    })
    .addField({
        name: "domain",
        type: "string",
        required: true,
        from: [
            {
                provider: "configuration",
                compute: (): string =>
                    resolveDomain(
                        config.get("environmentName"),
                        () => process.env["NOVA_DOMAIN"] ?? "http://localhost:3000"
                    ),
            },
        ],
    })
    .addField({
        name: "awsUserPoolId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_USER_POOL_ID",
            },
        ],
    })

    .addField({
        name: "stripeSecretKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_STRIPE_SECRET_KEY",
            },
        ],
    })
    .addField({
        name: "stripeWebhookKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_STRIPE_WEBHOOK_KEY",
            },
        ],
    })
    .addField({
        name: "ddAppId",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_DD_APP_ID",
            },
        ],
    })
    .addField({
        name: "ddClientToken",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_DD_CLIENT_TOKEN",
            },
        ],
    })
    .addField({
        name: "ddSite",
        type: "string",
        required: true,
        from: [
            {
                provider: "configuration",
                value: "us3.datadoghq.com",
            },
        ],
    })
    .addField({
        name: "agoraPublicAppId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_AGORA_APP_ID",
            },
        ],
    })
    .addField({
        name: "agoraAppCertificate",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "AGORA_APP_CERTIFICATE",
            },
        ],
    })
    .addField({
        name: "agoraCustomerId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "AGORA_CUSTOMER_ID",
            },
        ],
    })
    .addField({
        name: "agoraCustomerSecret",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "AGORA_CUSTOMER_SECRET",
            },
        ],
    })
    .addField({
        name: "agoraRTCWebhookSecret",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "AGORA_RTC_WEBHOOK_SECRET",
            },
        ],
    })
    .addField({
        name: "agoraCloudRecordingWebhookSecret",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "AGORA_CLOUD_RECORDING_WEBHOOK_SECRET",
            },
        ],
    })
    .addField({
        name: "agoraMediaGatewayWebhookSecret",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "AGORA_MEDIA_GATEWAY_WEBHOOK_SECRET",
            },
        ],
    })
    .addField({
        name: "novaDjiMediaUploadRoleArn",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_DJI_MEDIA_UPLOAD_ROLE_ARN",
            },
        ],
    })
    .addField({
        name: "novaIngestUploadRoleArn",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_INGEST_UPLOAD_ROLE_ARN",
            },
        ],
    })
    .addField({
        name: "cloudfrontKeyPairId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_CLOUDFRONT_KEY_PAIR_ID",
            },
        ],
    })
    .addField({
        name: "cloudfrontDomain",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_NOVA_CLOUDFRONT_DOMAIN",
            },
        ],
    })
    .addField({
        name: "cloudfrontPrivateKeySecretArn",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_CLOUDFRONT_PRIVATE_KEY_SECRET_ARN",
            },
        ],
    })
    .addField({
        name: "processingBucket",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_PROCESSING_BUCKET",
            },
        ],
    })
    .addField({
        name: "novaAgoraMediaUploadRoleArn",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AGORA_MEDIA_UPLOAD_ROLE_ARN",
            },
        ],
    })
    .addField({
        name: "agoraAwsAccessKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AGORA_AWS_ACCESS_KEY_ID",
            },
        ],
    })
    .addField({
        name: "agoraAwsSecretKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AGORA_AWS_SECRET_KEY",
            },
        ],
    })
    .addField({
        name: "slackAutomationsChannel",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "SLACK_AUTOMATIONS_CHANNEL_ID",
            },
        ],
    })
    .addField({
        name: "slackInternalAutomationsChannel",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "SLACK_HBD_CHANNEL_ID",
            },
        ],
    })
    .addField({
        name: "slackUserEventsChannel",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "SLACK_USER_EVENTS_CHANNEL_ID",
            },
        ],
    })
    .addField({
        name: "slackFlightSyncEventsChannel",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "SLACK_FLIGHT_SYNC_EVENTS_CHANNEL_ID",
            },
        ],
    })
    .addField({
        name: "slackStitchAutomationsChannel",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "SLACK_STITCH_AUTOMATIONS_CHANNEL_ID",
            },
        ],
    })
    .addField({
        name: "slackAPIToken",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "SLACK_API_TOKEN_ID",
            },
        ],
    })
    .addField({
        name: "redisEndpoint",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_REDIS_ENDPOINT",
            },
        ],
    })
    .addField({
        name: "devMetrics",
        type: "string",
        default: "",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NEXT_PUBLIC_DEV_ENABLE_METRICS",
            },
        ],
    })
    .addField({
        name: "googleAnalyticsSecret",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_GA_API_SECRET",
            },
        ],
    })
    .addField({
        name: "novaCookiesDomain",
        type: "string",
        required: true,
        from: [
            {
                provider: "configuration",
                production: "app.mapnova.com",
                staging: "staging.mapnova.com",
                preview: "preview.mapnova.com",
                alpha: "alpha.mapnova.com",
                localhost: "localhost",
            },
        ],
    })
    .addField({
        name: "aircastAppConfigAppId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPCONFIG_APP_ID",
            },
        ],
    })
    .addField({
        name: "aircastAppConfigEnvironmentId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPCONFIG_ENVIRONMENT_ID",
            },
        ],
    })
    .addField({
        name: "aircastAppConfigVersionConfigId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPCONFIG_VERSION_CONFIG_ID",
            },
        ],
    })
    .addField({
        name: "aircastAppConfigFeatureFlagsConfigId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPCONFIG_FEATURE_FLAGS_CONFIG_ID",
            },
        ],
    })
    .addField({
        name: "atlasAppConfigAppId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPCONFIG_ATLAS_APP_ID",
            },
        ],
    })
    .addField({
        name: "atlasAppConfigEnvironmentId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPCONFIG_ATLAS_ENVIRONMENT_ID",
            },
        ],
    })
    .addField({
        name: "atlasAppConfigVersionConfigId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPCONFIG_ATLAS_VERSION_CONFIG_ID",
            },
        ],
    })
    .addField({
        name: "atlasAppConfigFeatureFlagsConfigId",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_APPCONFIG_ATLAS_FEATURE_FLAGS_CONFIG_ID",
            },
        ],
    })
    .addField({
        name: "djiApiKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_DJI_API_KEY",
            },
        ],
    })
    .addField({
        name: "integrationEncryptionKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_AWS_INTEGRATION_ENCRYPTION_KEY",
            },
        ],
    })
    .addField({
        name: "pgUsername",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "PG_USERNAME",
            },
        ],
    })
    .addField({
        name: "pgHost",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "PG_URL",
            },
        ],
    })
    .addField({
        name: "pgDatabase",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "PG_DATABASE",
            },
        ],
    })
    .addField({
        name: "socketServerPort",
        type: "string",
        required: true,
        default: "3001",
        from: [
            {
                provider: "environment",
                name: "NOVA_SOCKET_SERVER_PORT",
            },
        ],
    })
    .addField({
        name: "apiGatewayPort",
        type: "string",
        required: true,
        default: "3003",
        from: [
            {
                provider: "environment",
                name: "NOVA_API_GATEWAY_PORT",
            },
        ],
    })
    .addField({
        name: "socketServerUrl",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_SOCKET_SERVER_URL",
            },
        ],
    })
    .addField({
        name: "liveVideoServiceDomain",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_LIVE_VIDEO_SERVICE_DOMAIN",
            },
        ],
    })
    .addField({
        name: "liveVideoServiceUrl",
        type: "string",
        required: true,
        from: [
            {
                provider: "configuration",
                compute: (): string => `https://${config.get("liveVideoServiceDomain")}`,
            },
        ],
    })
    .addField({
        name: "integrationTelemetryServiceUrl",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_INTEGRATION_TELEMETRY_SERVER_URL",
            },
        ],
    })
    .addField({
        name: "datadogMetricsApiKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_DATADOG_METRICS_API_KEY",
            },
        ],
    })
    .addField({
        name: "datadogMetricsAppKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_DATADOG_METRICS_APP_KEY",
            },
        ],
    })
    .addField({
        name: "ptChecklistTokenSecret",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_PT_CHECKLIST_TOKEN_SECRET",
            },
        ],
    })
    .addField({
        name: "anthropicApiKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "ANTHROPIC_API_KEY",
            },
        ],
    })
    .addField({
        name: "xweatherClientId",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NOVA_XWEATHER_CLIENT_ID",
            },
        ],
    })
    .addField({
        name: "xweatherClientSecret",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NOVA_XWEATHER_CLIENT_SECRET",
            },
        ],
    })
    .addField({
        name: "weatherOriginToken",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_WEATHER_ORIGIN_TOKEN",
            },
        ],
    })
    .addField({
        name: "safeSkyApiKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_SAFESKY_API_KEY",
            },
        ],
    })
    .addField({
        name: "flightRadar24ApiKey",
        type: "string",
        required: true,
        from: [
            {
                provider: "environment",
                name: "NOVA_FLIGHTRADAR24_API_KEY",
            },
        ],
    })
    .addField({
        name: "weatherCacheValidFrom",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NOVA_WEATHER_CACHE_VALID_FROM",
            },
        ],
    })
    .addField({
        name: "prewarmWeatherCloudfront",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "PREWARM_WEATHER_CLOUDFRONT",
            },
        ],
    })
    .addField({
        name: "useStrikeBudgetBank",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NOVA_STRIKE_BUDGET_BANK_ENABLED",
            },
        ],
    })
    .addField({
        name: "strikeReadBucket",
        type: "string",
        required: false,
        from: [
            {
                provider: "environment",
                name: "NOVA_STRIKE_READ_BUCKET",
            },
        ],
    })
    .addField({
        name: "sqsFlightRecordsDjiUrl",
        type: "string",
        required: false,
        from: [{ provider: "environment", name: "NOVA_AWS_SQS_FLIGHT_RECORDS_DJI_URL" }],
    })
    .addField({
        name: "sqsFlightRecordsAutelUrl",
        type: "string",
        required: false,
        from: [{ provider: "environment", name: "NOVA_AWS_SQS_FLIGHT_RECORDS_AUTEL_URL" }],
    })
    .addField({
        name: "sqsFlightRecordsSkydioUrl",
        type: "string",
        required: false,
        from: [{ provider: "environment", name: "NOVA_AWS_SQS_FLIGHT_RECORDS_SKYDIO_URL" }],
    })
    .addField({
        name: "sqsCsvLogbookImportUrl",
        type: "string",
        required: false,
        from: [{ provider: "environment", name: "NOVA_AWS_SQS_CSV_LOGBOOK_IMPORT_URL" }],
    })
    .addField({
        name: "faaNmsClientId",
        type: "string",
        required: false,
        from: [{ provider: "environment", name: "FAA_NMS_CLIENT_ID" }],
    })
    .addField({
        name: "faaNmsClientSecret",
        type: "string",
        required: false,
        from: [{ provider: "environment", name: "FAA_NMS_CLIENT_SECRET" }],
    })
    .addField({
        name: "faaNmsBaseUrl",
        type: "string",
        required: false,
        from: [{ provider: "environment", name: "FAA_NMS_BASE_URL" }],
    })

export const isLocalhostEnvironment = () => config.get("environmentName") === "localhost"

export const isPrimaryRegion = () => config.get("awsRegion") === PRIMARY_REGION

export const getApiGatewayDomainForRegion = (
    region: SupportedRegions = config.get("awsRegion") as SupportedRegions,
    environment: NovaEnvironment = config.get("environmentName") as NovaEnvironment
) => {
    if (environment === "localhost") {
        return `${config.get("domain")}/api-gateway`
    }
    return `https://${region.split("-")[0]}.api.${getDomainPrefix(environment)}mapnova.com`
}

export const currentRegion = () => config.get("awsRegion") as SupportedRegions

export const makeNextJsUrl = () => {
    const environment = config.get("environmentName")
    return environment === "localhost"
        ? "http://localhost:3000"
        : `https://${environment === "production" ? "app" : environment}.mapnova.com`
}

export type NodeFacadeConforms = Conforms<typeof import("./node")>
